import React from 'react'
import {updateFactory} from '@modifi/redux'
import {aPush} from '@modifi/router'
import {PersonLegalRole} from '@modifi/enums'
import {aAddSnackBar} from '@modifi/plugin-snackbars'
import {LegalRepresentativeView} from '@modifi/onboarding-api-clients'
import {
  selectPublicRegisteredCompanyLegalRepresentatives,
  selectPublicRegisteredCompanyUltimateBeneficialOwners,
} from '../../../../data/public-registered-companies'
import {LEGAL_REPRESENTATIVES_ENTITY, ULTIMATE_BENEFICIAL_OWNERS_ENTITY} from '../constants'
import {aCreatePerson, pickPersonFields} from '../../../../modules/persons'
import {aLinkPersonToLegalRole, pickPropertyKeysByLegalRole} from '../../../../modules/legal-roles'
import {TranslatedString} from '../../../../utils'
import {
  ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS,
  ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS_CREATE,
  ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES,
  ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES_CREATE,
  ROUTE_ONBOARDING,
} from '../../../../routes/constants'
import {SECTION_LEGAL_REPRESENTATIVES} from '../../../../constants/sections'
import {aFetchLegalRepresentatives} from '../../../../data/legal-representatives'
import {aFetchBeneficialOwners} from '../../../../data/beneficial-owners'

const {reducer, actions, selectors} = updateFactory({
  actionNamePrefix: 'pages/data-import/submit',
  rootSelector: state => state?.pages?.dataImport?.submit,
  submitRequest:
    (a, changes: Record<string, any>) =>
    async (dispatch, getState): Promise<void> => {
      dispatch(a.pendingAction())

      const legalRepsToCreate = selectPublicRegisteredCompanyLegalRepresentatives(
        getState()
      ).filter((legalRep: {providerPersonId: string}) =>
        changes[LEGAL_REPRESENTATIVES_ENTITY]?.includes(legalRep.providerPersonId)
      )
      const beneficialOwnersToCreate = selectPublicRegisteredCompanyUltimateBeneficialOwners(
        getState()
      ).filter((beneficialOwner: {providerPersonId: string}) =>
        changes[ULTIMATE_BENEFICIAL_OWNERS_ENTITY]?.includes(beneficialOwner.providerPersonId)
      )

      let failures = 0
      let lastError: any
      await Promise.all([
        ...legalRepsToCreate.map(async (legalRep: LegalRepresentativeView) => {
          try {
            // @ts-expect-error to be fixed
            const createdPerson = await dispatch(aCreatePerson(pickPersonFields(legalRep)))
            await dispatch(
              aLinkPersonToLegalRole(
                // @ts-expect-error to be fixed
                createdPerson.externalId,
                PersonLegalRole.LEGAL_REPRESENTATIVE,
                pickPropertyKeysByLegalRole(PersonLegalRole.LEGAL_REPRESENTATIVE, legalRep)
              )
            )
          } catch (e: any) {
            failures += 1
            lastError = e
            dispatch(
              aAddSnackBar({
                message: (
                  <TranslatedString
                    i18nKey="person-import-failed.alert"
                    values={{userName: `${legalRep?.firstName} ${legalRep?.lastName}`}}
                  />
                ),
                type: 'alert',
              })
            )
          }
        }),
        ...beneficialOwnersToCreate.map(async (legalRep: LegalRepresentativeView) => {
          try {
            // @ts-expect-error to be fixed
            const createdPerson = await dispatch(aCreatePerson(pickPersonFields(legalRep)))
            await dispatch(
              aLinkPersonToLegalRole(
                // @ts-expect-error type not generated properly
                createdPerson.externalId,
                PersonLegalRole.BENEFICIAL_OWNER,
                pickPropertyKeysByLegalRole(PersonLegalRole.BENEFICIAL_OWNER, legalRep)
              )
            )
          } catch (e: any) {
            failures += 1
            lastError = e
            dispatch(
              aAddSnackBar({
                message: (
                  <TranslatedString
                    i18nKey="person-import-failed.alert"
                    values={{userName: `${legalRep?.firstName} ${legalRep?.lastName}`}}
                  />
                ),
                type: 'alert',
              })
            )
          }
        }),
      ])

      if (failures > 0) {
        dispatch(a.errorAction(lastError))
        if (failures === legalRepsToCreate.length + beneficialOwnersToCreate.length) {
          // all failed
          dispatch(
            aAddSnackBar({
              message: <TranslatedString i18nKey="person-import-failed-all.alert" />,
              type: 'alert',
            })
          )
        } else {
          dispatch(
            aAddSnackBar({
              message: <TranslatedString i18nKey="person-import-failed.warn" />,
              type: 'warn',
            })
          )
        }
      } else {
        dispatch(a.successAction())
        dispatch(
          aAddSnackBar({
            message: <TranslatedString i18nKey="person-import.success" />,
            type: 'success',
          })
        )
      }

      if (legalRepsToCreate.length) {
        await dispatch(aFetchLegalRepresentatives())
        dispatch(aPush(ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES))
      } else if (beneficialOwnersToCreate.length) {
        await dispatch(aFetchBeneficialOwners())
        dispatch(aPush(ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS))
      } else {
        dispatch(aPush(ROUTE_ONBOARDING))
      }
    },
})

export default reducer

export const {selectUpdatePending, selectUpdateHasError} = selectors

export const {aSubmit} = actions

export const aSkip = (personRoleType: string) =>
  aPush(
    personRoleType === SECTION_LEGAL_REPRESENTATIVES
      ? ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES_CREATE
      : ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS_CREATE
  )
